import type { LaneId } from "@fate-kingdom/engine";

import type {
  AlphaBattlefieldCameraMode,
  AlphaBattlefieldCameraState,
} from "../camera/battlefield-camera-types";

export type PerspectiveViewportClass = "compact-portrait" | "portrait" | "landscape";

export interface PerspectiveCameraCalibration {
  readonly viewportClass: PerspectiveViewportClass;
  readonly fov: number;
  readonly position: readonly [number, number, number];
  readonly target: readonly [number, number, number];
  readonly dpr: readonly [number, number];
  readonly laneSpread: number;
}

const LANE_FOCUS_X: Readonly<Record<LaneId, number>> = {
  left: -2.55,
  center: 0,
  right: 2.55,
};

function modeOffset(mode: AlphaBattlefieldCameraMode): {
  readonly height: number;
  readonly distance: number;
  readonly targetZ: number;
} {
  if (mode === "combat") return { height: -0.55, distance: -1.15, targetZ: -0.65 };
  if (mode === "attack") return { height: -0.25, distance: -0.72, targetZ: -0.42 };
  if (mode === "placement") return { height: -0.18, distance: -0.48, targetZ: 0.7 };
  if (mode === "player") return { height: -0.3, distance: -0.62, targetZ: 1.05 };
  if (mode === "opponent") return { height: -0.12, distance: -0.52, targetZ: -1.35 };
  if (mode === "inspect") return { height: 0.2, distance: 0.25, targetZ: 0.35 };
  if (mode === "result") return { height: -0.35, distance: -0.82, targetZ: -0.35 };
  return { height: 0, distance: 0, targetZ: 0 };
}

export function perspectiveViewportClass(width: number, height: number): PerspectiveViewportClass {
  if (height <= width) return "landscape";
  return width <= 340 || height <= 680 ? "compact-portrait" : "portrait";
}

export function getPerspectiveCameraCalibration({
  width,
  height,
  cameraState,
}: {
  readonly width: number;
  readonly height: number;
  readonly cameraState: AlphaBattlefieldCameraState;
}): PerspectiveCameraCalibration {
  const viewportClass = perspectiveViewportClass(width, height);
  const portrait = viewportClass !== "landscape";
  const compact = viewportClass === "compact-portrait";
  const mode = modeOffset(cameraState.mode);
  const focusX =
    cameraState.focusLaneId === undefined ? 0 : LANE_FOCUS_X[cameraState.focusLaneId] * 0.18;
  const baseHeight = portrait ? (compact ? 10.9 : 10.3) : 6.35;
  const baseDistance = portrait ? (compact ? 17 : 15.7) : 10.35;
  const panZ = cameraState.panY * 1.35;
  const manualPositionX = cameraState.panX * 0.45;
  const manualTargetX = cameraState.panX * 1.7;
  const target: readonly [number, number, number] = [
    focusX * 1.8 + manualTargetX,
    0.05,
    -0.45 + panZ + mode.targetZ,
  ];
  const undolledPosition: readonly [number, number, number] = [
    focusX + manualPositionX,
    baseHeight + mode.height,
    baseDistance + mode.distance,
  ];
  const zoom = Math.max(0.01, cameraState.zoom);
  const position: readonly [number, number, number] = [
    target[0] + (undolledPosition[0] - target[0]) / zoom,
    target[1] + (undolledPosition[1] - target[1]) / zoom,
    target[2] + (undolledPosition[2] - target[2]) / zoom,
  ];

  return {
    viewportClass,
    fov: portrait ? 40 : 39,
    position,
    target,
    dpr: portrait ? [1, 1.5] : [1, 2],
    laneSpread: perspectiveLaneSpread(width, height),
  };
}

export function perspectiveLaneX(laneId: LaneId, laneSpread = 2.55): number {
  if (laneId === "left") return -laneSpread;
  if (laneId === "right") return laneSpread;
  return 0;
}

export function perspectiveBoardScale(
  width: number,
  height: number,
): readonly [number, number, number] {
  const viewportClass = perspectiveViewportClass(width, height);
  if (viewportClass === "compact-portrait") return [0.55, 1, 1];
  if (viewportClass === "portrait") return [0.6, 1, 1];
  return [1, 1, 1];
}

export function perspectiveLaneSpread(width: number, height: number): number {
  return 2.55 * perspectiveBoardScale(width, height)[0];
}
